import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SmartShell } from '../../components/smart/SmartShell';
import { Stepper } from '../../components/smart/Stepper';
import { ValueBar } from '../../components/smart/ValueBar';
import { Button } from '../../components/smart/ui/Button';
import { cn } from '../../components/smart/utils/cn';
import { SMART_ROUTES } from '../../constants';
import {
  smartMockQuote,
  smartMockRecipients,
} from '../../gallery/smartMock';

/**
 * Gallery-static recipient name step — recent picks from mock, free-text
 * continue navigates to summary (skips payout/funding data substeps).
 */
export const CollectRecipientPage = () => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('Maria Lopez Garcia');

  const trimmed = fullName.trim();
  const valid = trimmed.split(/\s+/).length >= 2 && trimmed.length >= 3;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    navigate(SMART_ROUTES.COLLECT_SUMMARY);
  };

  return (
    <SmartShell
      title={t('wizard.headline.collect.title.default')}
      subtitle={t('wizard.headline.collect.subtitle')}
    >
      <div className="flex flex-col gap-5">
        <Stepper
          steps={[
            { id: 'recipient_identity', label: t('wizard.collect.stepLabel.recipient_identity') },
            { id: 'recipient_payout', label: t('wizard.collect.stepLabel.recipient_payout') },
            { id: 'funding_data', label: t('wizard.collect.stepLabel.funding_data') },
            { id: 'summary', label: t('wizard.collect.stepLabel.summary') },
          ]}
          activeIndex={0}
          completedIndices={[]}
          onJump={(i) => {
            if (i === 3) navigate(SMART_ROUTES.COLLECT_SUMMARY);
          }}
        />

        <div className="border-border bg-bg-elevated rounded-2xl border p-5">
          <h2 className="text-lg font-semibold tracking-tight">
            {t('wizard.collect.recipientName.title')}
          </h2>
          <p className="text-fg-muted mt-1 text-sm">
            {t('wizard.collect.recipientName.description')}
          </p>

          {smartMockRecipients.length > 0 && (
            <div className="mt-4 flex flex-col gap-2">
              <span className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
                {t('wizard.collect.recipientName.recentLabel')}
              </span>
              {smartMockRecipients.map((r) => (
                <button
                  key={r.recipientKey}
                  type="button"
                  onClick={() => {
                    setFullName(r.fullName);
                    navigate(SMART_ROUTES.COLLECT_SUMMARY);
                  }}
                  className={cn(
                    'border-border bg-bg text-fg rounded-xl border px-4 py-3 text-left text-sm font-medium',
                    'hover:border-fg-muted transition-colors',
                  )}
                >
                  {r.fullName}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-fg-muted text-[11px] font-semibold uppercase tracking-wide">
                {t('wizard.collect.recipientName.fullNameLabel')}
              </span>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="border-border bg-bg text-fg focus:ring-accent rounded-xl border px-4 py-3 text-base focus:outline-none focus:ring-2"
                autoComplete="name"
              />
            </label>
            <Button type="submit" fullWidth size="lg" disabled={!valid}>
              {t('common.continue')}
            </Button>
          </form>
        </div>

        <div className="flex justify-start">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(SMART_ROUTES.WIZARD_PAYOUT)}
          >
            ← {t('common.back')}
          </Button>
        </div>

        <ValueBar
          quote={smartMockQuote}
          prefix={t('wizard.collect.valueBarPrefix.send')}
        />
      </div>
    </SmartShell>
  );
};
